/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box, Divider, Stack, Typography } from '@mui/material';
import { type ReactNode, Children } from 'react';
import { kebabCase } from 'lodash';
import { LabelledSwitch } from '../../LabelledSwitch/LabelledSwitch';
import { Label } from '../../Label/Label';

type ToolbarConfigProps = {
	title: string;
	description: string;
	value: boolean;
	onChange: ( prevValue: boolean ) => void;
	image: string;
	imageActive: string;
	children?: ReactNode;
	disabled?: boolean;
};

export function ToolbarConfig( {
	title,
	description,
	children,
	value,
	onChange,
	image,
	imageActive,
	disabled
}: ToolbarConfigProps ): ReactNode {
	const toolbarContainerId = `toolbars-container-${ kebabCase( title ) }`;
	const toggleId = `toolbars-toggle-${ kebabCase( title ) }`;
	const childrenArray = Children.toArray( children );
	const shouldRenderChildren = childrenArray.some( child => !!child );

	return (
		<Stack id={ toolbarContainerId } sx={ { border: '1px solid', borderColor: 'secondary.100', borderRadius: '4px' } }>
			<Stack flexDirection={ 'row' } justifyContent={ 'space-between' } padding={ '10px' }>
				<Box sx={ { display: 'flex', gap: '10px' } }>
					<Box sx={ {
						width: '115px'
					} }
					>
						<img src={ value ? imageActive : image }/>
					</Box>
					<Box>
						<Typography
							htmlFor={ toggleId }
							component="label"
							color={ 'text.primary' }
							fontSize={ '14px' }
							fontWeight={ 700 }
							lineHeight={ '22px' }
							onClick={ () => !disabled && onChange( !value ) }
						>
							{ title }
						</Typography>
						<Typography color={ 'text.secondary' } fontSize={ '12px' } fontWeight={ 400 } lineHeight={ '20px' }>
							{ description }
						</Typography>
					</Box>
				</Box>
				{ disabled ? (
					<Label text={ value ? 'active' : 'inactive' }/>
				) : (
					<LabelledSwitch
						id={ toggleId }
						disabled={ disabled }
						text={ 'OFF' }
						textActive={ 'ON' }
						active={ value }
						onToggle={ () => onChange( !value ) }
						width='28px'
					/>
				) }
			</Stack>
			{ /* To avoid fragments. */ }
			{ shouldRenderChildren && (
				<Divider/>
			) }
			{ shouldRenderChildren && (
				<Stack gap={ '10px' } padding={ '10px' }>
					{ children }
				</Stack>
			) }
		</Stack>
	);
}
